import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Badge,
} from "react-bootstrap";
import { useRegisterAcademy } from "../hooks/useRegisterAcademy";
import { useAcademyTypes } from "../hooks/useAcademyTypes";
import { NewPasswordFields } from "../../../components/NewPasswordFields";
import { ConsentCheckbox } from "../../../app/privacy/ConsentCheckbox";

function RegisterAcademy() {
  const [searchParams] = useSearchParams();
  // Plan chosen on the landing pricing cards (?plan=Academia). Optional — the
  // backend falls back to the free tier (Piloto) when absent or unrecognized.
  const requestedPlan = searchParams.get("plan");

  const [ownerName, setOwnerName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordValid, setPasswordValid] = useState(false);
  const [academyName, setAcademyName] = useState("");
  const [identification, setIdentification] = useState("");
  const [academyType, setAcademyType] = useState("");
  const [consent, setConsent] = useState(false);
  const [successSlug, setSuccessSlug] = useState(null);
  const [successPlan, setSuccessPlan] = useState(null);

  const { mutate, isPending, isError, error } = useRegisterAcademy();
  const { data: academyTypes = [], isLoading: typesLoading } = useAcademyTypes();

  function handleSubmit(e) {
    e.preventDefault();
    mutate(
      {
        owner: { name: ownerName, email, password },
        academy: { name: academyName, identification, type: academyType },
        plan: requestedPlan || undefined,
      },
      {
        onSuccess(data) {
          setSuccessSlug(data.slug);
          setSuccessPlan(data.plan_name);
        },
      }
    );
  }

  if (successSlug) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center min-vh-100"
        style={{ padding: "1rem" }}
      >
        <Card className="w-100 text-center shadow-sm" style={{ maxWidth: 480 }}>
          <Card.Body className="p-4">
            <h5>¡Academia registrada!</h5>
            <p className="text-muted mt-2">
              Revisa tu correo electrónico para activar tu cuenta.
            </p>
            {successPlan && (
              <p className="text-muted small mb-2">
                Plan activado: <strong>{successPlan}</strong>
              </p>
            )}
            <Link
              to={`/${successSlug}/login`}
              className="btn btn-primary mt-2"
            >
              Ir a iniciar sesión
            </Link>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const fieldErrors = error?.response?.data?.field_errors ?? {};
  const globalError =
    isError && !Object.keys(fieldErrors).length
      ? error?.response?.data?.message ||
        "Error al registrar la academia. Verifica los datos."
      : null;

  return (
    <Container
      className="d-flex justify-content-center align-items-center min-vh-100"
      style={{ padding: "1rem" }}
    >
      <Card className="w-100 shadow-sm" style={{ maxWidth: 540 }}>
        <Card.Body className="p-4">
          <h4 className="mb-1">Registra tu academia</h4>
          <p className="text-muted mb-3">
            Crea tu cuenta de propietario y empieza hoy.
            {requestedPlan && (
              <>
                {" "}
                <Badge bg="primary">Plan {requestedPlan}</Badge>
              </>
            )}
          </p>

          {globalError && <Alert variant="danger">{globalError}</Alert>}

          <Form onSubmit={handleSubmit} noValidate>
            {/* ── Propietario ───────────────────────────────────── */}
            <p
              className="text-uppercase text-muted mb-2"
              style={{ fontSize: "0.7rem", letterSpacing: 1 }}
            >
              Datos del propietario
            </p>

            <Form.Group className="mb-3" controlId="ownerName">
              <Form.Label>Nombre completo</Form.Label>
              <Form.Control
                type="text"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                required
                placeholder="Ana Ruiz"
                isInvalid={Boolean(fieldErrors["owner.name"])}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors["owner.name"]}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="ownerEmail">
              <Form.Label>Correo electrónico</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="ana@academia.com"
                isInvalid={Boolean(fieldErrors["owner.email"])}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors["owner.email"]}
              </Form.Control.Feedback>
            </Form.Group>

            <NewPasswordFields
              value={password}
              onChange={setPassword}
              onValidityChange={setPasswordValid}
              passwordId="ownerPassword"
              errorText={fieldErrors["owner.password"]}
            />

            {/* ── Academia ──────────────────────────────────────── */}
            <p
              className="text-uppercase text-muted mb-2"
              style={{ fontSize: "0.7rem", letterSpacing: 1 }}
            >
              Datos de la academia
            </p>

            <Row>
              <Col xs={12} sm={7}>
                <Form.Group className="mb-3" controlId="academyName">
                  <Form.Label>Nombre de la academia</Form.Label>
                  <Form.Control
                    type="text"
                    value={academyName}
                    onChange={(e) => setAcademyName(e.target.value)}
                    required
                    placeholder="English House"
                    isInvalid={Boolean(fieldErrors["academy.name"])}
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors["academy.name"]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={12} sm={5}>
                <Form.Group className="mb-3" controlId="academyType">
                  <Form.Label>Tipo</Form.Label>
                  <Form.Select
                    value={academyType}
                    onChange={(e) => setAcademyType(e.target.value)}
                    required
                    disabled={typesLoading}
                    isInvalid={Boolean(fieldErrors["academy.type"])}
                  >
                    <option value="">
                      {typesLoading ? "Cargando…" : "Selecciona un tipo"}
                    </option>
                    {academyTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors["academy.type"]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="academyIdentification">
              <Form.Label>NIT / Identificación</Form.Label>
              <Form.Control
                type="text"
                value={identification}
                onChange={(e) => setIdentification(e.target.value)}
                required
                placeholder="900123456"
                isInvalid={Boolean(fieldErrors["academy.identification"])}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors["academy.identification"]}
              </Form.Control.Feedback>
            </Form.Group>

            <ConsentCheckbox checked={consent} onChange={setConsent} />

            <Button
              type="submit"
              variant="primary"
              className="w-100"
              disabled={isPending || !consent || !passwordValid}
            >
              {isPending ? "Registrando…" : "Crear academia"}
            </Button>
          </Form>

          <p
            className="text-center mt-3 mb-0 text-muted"
            style={{ fontSize: "0.875rem" }}
          >
            ¿Ya tienes academia?{" "}
            <span>Usa el enlace de tu academia para iniciar sesión.</span>
          </p>
        </Card.Body>
      </Card>
    </Container>
  );
}

export { RegisterAcademy };
